import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import './UseCases.css';

const UseCases = () => {
  const useCasesRef = useRef(null);

  useEffect(() => {
    const isMobile = window.innerWidth <= 768;
    
    if (isMobile) {
      // On mobile, immediately show all elements
      const elements = useCasesRef.current?.querySelectorAll('.fade-in');
      elements?.forEach((el) => {
        el.classList.add('visible');
      });
      return;
    }
    
    // Desktop intersection observer with mobile-friendly settings
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { 
        threshold: 0.1,                    // Lower threshold
        rootMargin: '0px 0px 20px 0px'     // Positive root margin for earlier trigger
      }
    );

    const elements = useCasesRef.current?.querySelectorAll('.fade-in');
    elements?.forEach((el) => observer.observe(el));

    // Handle window resize to re-evaluate mobile state
    const handleResize = () => {
      if (window.innerWidth <= 768) {
        observer.disconnect();
        const elements = useCasesRef.current?.querySelectorAll('.fade-in');
        elements?.forEach((el) => {
          el.classList.add('visible');
        });
      }
    };
    
    window.addEventListener('resize', handleResize);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const useCases = [
    {
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M3 21h18M9 8h1M9 12h1M9 16h1M14 8h1M14 12h1M14 16h1M5 21V5a2 2 0 012-2h10a2 2 0 012 2v16" />
        </svg>
      ),
      title: "Enterprise Legacy Systems",
      link: "/use-cases/desktop-testing",
      description: "Transform testing for mission-critical Windows desktop applications that power your business operations. Qate AI bridges the automation gap for SAP GUI, Oracle Forms, Microsoft Dynamics, custom ERP systems, and proprietary thick-client applications that web-only testing tools simply cannot reach. Eliminate manual testing bottlenecks while ensuring reliability for systems handling millions in transactions daily.",
      details: "Perfect for organizations running complex desktop environments where application downtime costs thousands per minute. Our AI understands desktop UI elements, handles complex workflows, and maintains test stability across application updates.",
      examples: ["SAP GUI Testing", "Oracle Forms", "Custom Desktop Apps", "ERP Systems", "Trading Platforms"]
    },
    {
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      ),
      title: "Modern Development Teams",
      link: "/use-cases/web-testing",
      description: "Accelerate agile development cycles with conversational test creation that keeps pace with rapid feature deployments. Transform complex testing requirements into natural language conversations, enabling product managers and business analysts to create comprehensive test suites without coding expertise. Perfect for teams building React, Angular, Vue applications and modern SaaS platforms.",
      details: "Reduce sprint bottlenecks by democratizing test creation across your entire team. QA engineers focus on strategy while non-technical stakeholders contribute directly to test coverage, dramatically improving release velocity and quality.",
      examples: ["SaaS Platforms", "E-commerce Sites", "Customer Portals", "Mobile Web Apps", "API Testing"]
    },
    {
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
      ),
      title: "Digital Transformation",
      link: "/use-cases/regression-testing",
      description: "Navigate complex modernization projects with confidence by testing both legacy Windows applications and new web systems simultaneously. Qate AI provides comprehensive coverage during critical migration phases, ensuring business continuity while validating new system functionality. Eliminate the testing gaps that cause expensive rollbacks and delayed deployments.",
      details: "Essential for organizations modernizing core business systems. Validate data migration accuracy, test hybrid workflows spanning old and new systems, and ensure seamless user experience transitions without compromising operational stability.",
      examples: ["Legacy Modernization", "Hybrid Workflows", "Migration Testing", "System Integration", "Data Validation"]
    },
    {
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
        </svg>
      ),
      title: "Quality Assurance Teams",
      link: "/use-cases/ci-cd-integration",
      description: "Revolutionize QA operations by eliminating the 80% of time traditionally spent on test maintenance and flaky test debugging.¹ Our self-healing architecture automatically adapts to UI changes, while conversational test creation enables rapid expansion of test coverage. Transform your QA team from maintenance-focused to strategy-focused, driving higher quality releases with fewer resources.",
      sourceUrl: "https://www.rainforestqa.com/blog/test-automation-maintenance",
      details: "Ideal for QA teams struggling with test maintenance overhead and slow manual testing cycles. Increase test coverage by 300% while reducing maintenance effort, enabling comprehensive regression testing and faster feedback loops for development teams.",
      examples: ["Regression Testing", "User Acceptance", "Cross-Platform Validation", "Smoke Testing", "Performance Testing"]
    }
  ];

  return (
    <section className="use-cases" ref={useCasesRef}>
      <div className="use-cases-container">
        <div className="section-header fade-in">
          <h2>Perfect for Every Testing Challenge</h2>
          <p>From legacy Windows applications to modern web platforms - Qate AI covers it all</p>
        </div>
        
        <div className="use-cases-grid fade-in">
          {useCases.map((useCase, index) => (
            <div key={index} className={`use-case-card ${index === 0 || index === 1 ? 'featured' : ''}`}>
              <div className="use-case-icon">{useCase.icon}</div>
              <h3>{useCase.title}</h3>
              <p className="main-description">{useCase.description}</p>
              <p className="details-description">{useCase.details}</p>
              <div className="examples">
                {useCase.examples.map((example, i) => (
                  <span key={i} className="example-tag">{example}</span>
                ))}
              </div>
              {useCase.link && (
                <Link to={useCase.link} className="use-case-link">
                  Learn more <span>&rarr;</span>
                </Link>
              )}
              {useCase.sourceUrl && (
                <a
                  href={useCase.sourceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="source-link"
                >
                  ¹ Source
                </a>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default UseCases;